import React, { useState, useEffect } from 'react';

const TableOfContents = ({ contentRef }) => {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (!contentRef?.current) return;

    let observer = null;
    let timeoutId = null;

    // Use a more robust approach with multiple attempts
    const extractHeadings = () => {
      const headingElements = contentRef.current.querySelectorAll('h1, h2');
      if (headingElements.length === 0) {
        // If no headings found, try again after a short delay (max 3 attempts)
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(extractHeadings, 300);
        return;
      }

      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      const headingList = Array.from(headingElements).map((heading, index) => {
        // Create a more readable ID based on the heading text
        const textSlug = heading.textContent
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .trim();
        const id = textSlug || `heading-${index}`;
        
        // Only set ID if it doesn't already have one
        if (!heading.id) {
          heading.id = id;
        }
        
        return {
          id: heading.id,
          text: heading.textContent,
          level: parseInt(heading.tagName.charAt(1))
        };
      });

      setHeadings(headingList);

      // Clean up existing observer
      if (observer) {
        observer.disconnect();
      }

      // Set up intersection observer to track active heading
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveId(entry.target.id);
            }
          });
        },
        {
          rootMargin: '-20% 0% -35% 0%',
          threshold: 0
        }
      );

      headingElements.forEach((heading) => {
        observer.observe(heading);
      });
    };

    // Start extraction
    extractHeadings();

    // Cleanup function
    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [contentRef]);

  const scrollToHeading = (headingId) => {
    const element = document.getElementById(headingId);
    if (element) {
      // Get the actual header height dynamically
      const header = document.querySelector('header');
      const headerHeight = header ? header.offsetHeight : 80;
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - headerHeight - 20; // Extra 20px for better spacing

      try {
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth'
        });
        
        // Update active state immediately for better UX
        setActiveId(headingId);
      } catch (error) {
        console.warn('ScrollTo failed:', error);
        // Fallback to instant scroll
        window.scrollTo(0, Math.max(0, offsetPosition));
        setActiveId(headingId);
      }
    } else {
      console.warn(`Element with id "${headingId}" not found`);
    }
  };

  if (headings.length === 0) return null;

  return (
    <div className="w-full lg:sticky lg:top-24 lg:w-64 lg:pr-8">
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
          Table of Contents
        </h3>
        <nav className="space-y-1">
          {headings.map((heading) => (
            <button
              key={heading.id}
              onClick={() => scrollToHeading(heading.id)}
              className={`block w-full text-left px-2 py-1 text-sm rounded transition-colors ${
                heading.level === 1
                  ? 'font-semibold text-lg'
                  : heading.level === 2
                  ? 'font-medium ml-3'
                  : 'ml-6 text-gray-500'
              } ${
                activeId === heading.id
                  ? 'text-brand-600 bg-brand-50 border-l-2 border-brand-600'
                  : 'text-gray-700 hover:text-brand-600 hover:bg-gray-50'
              }`}
              title={heading.text}
            >
              {heading.text.length > 50 ? `${heading.text.substring(0, 50)}...` : heading.text}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default TableOfContents;
